import { MouseEvent } from "react";
import ActorImage from "./ActorImage";
import ActorInfo from "./ActorInfo";
import ActivityMeta from "./ActivityMeta";
import ActivityContextMenu from "./ActivityContextMenu";
import TranslationControls from "../../shared/TranslationControls";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesUp } from "@fortawesome/pro-solid-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import {
  Activity,
  ActivityObject,
  ActivityObjectType,
  ObjectWithContent,
} from "../../../@types/activities";
import { every, get, isEmpty, omit, some } from "lodash";
import { Activitystream } from "../../../api/activitystreams.ts";
import EmailRecommendedIndicator from "./EmailRecommendedIndicator.tsx";
import IBookmarkedIndicator from "./IBookmarkedIndicator.tsx";
import RecommendationIndicator from "./RecommendationIndicator.tsx";
import NewsletterIndicator from "./NewsletterIndicator.tsx";

type ActivityHeaderArgs = {
  activityId: string;
  actor: Activity["actor"];
  published: string;
  isNew?: boolean;
  activityObject: ActivityObject;
  translatedContent?: {
    language?: string;
    content?: string;
    name?: string;
    description?: string;
  };
  streamsFromActivity?: Activitystream[];
  foreignActivityStreams?: any[];
  renderedAsPinnedDuplicate?: boolean;
  sticky?: Activity["sticky"];
  error?: boolean;
  closeActivity?: (e: MouseEvent<HTMLButtonElement>) => void;
  emailRecommendationInfo?: {
    hasAttachments: boolean;
    totalAttachmentSize: number;
    lastRecommendation?: Activity["email_recommendation"];
  };
  actorUpdatedAt?: string | null;
  actorUpdatedBy?: { name: string } | null;
  archivedAt?: Activity["archived_at"];
  archivedByName?: Activity["archived_by_name"];
  emailRecommendationCount?: { internal: number; external: number };
  teaser?: string;
  emailRecommendedTo?: Activity["email_recommended_to"];
  emailRecommendedToMembers?: Activity["email_recommended_to_members"];
  externalEmailRecommendedTo?: Activity["external_email_recommended_to"];
  can: Activity["can"];
  sharedFrom?: Omit<Activity, "shared_from"> | null;
  repliesClosed?: Activity["replies_closed"];
  hidden?: Activity["hidden"];
  plannedForNewsletterAt?: Activity["planned_for_newsletter_at"];
  iSubscribedEmail: Activity["i_subscribed_email"];
  streamUrl?: string;
  canShare?: boolean;
  shareActivity?: () => void;
  sentInNewsLetterAt?: Activity["sent_in_newsletter_at"];
  type: ActivityObjectType;
  translateObject?: (
    data: { [key: string]: string },
    language: string,
    type?: string,
  ) => void;
  resetTranslation?: (language: string) => void;
  isHit?: boolean;
  external?: boolean;
};
export default function ActivityHeader({
  activityId,
  actor,
  activityObject,
  actorUpdatedAt,
  actorUpdatedBy,
  archivedAt,
  archivedByName,
  published,
  isNew = false,
  translatedContent,
  resetTranslation,
  translateObject,
  streamsFromActivity,
  foreignActivityStreams,
  renderedAsPinnedDuplicate = false,
  sticky = null,
  error = false,
  closeActivity,
  emailRecommendationCount,
  emailRecommendationInfo,
  teaser,
  emailRecommendedTo,
  emailRecommendedToMembers,
  externalEmailRecommendedTo,
  can,
  plannedForNewsletterAt,
  repliesClosed,
  hidden,
  iSubscribedEmail,
  streamUrl,
  canShare,
  shareActivity,
  sentInNewsLetterAt,
  type,
  isHit = false,
  external = false,
}: ActivityHeaderArgs) {
  const translateable = translateObject && resetTranslation;

  return (
    <div className="activity-header gap-4 flex mb-2">
      <div className="activity-actor-image py-4 shrink-0">
        <ActorImage {...actor} />
      </div>
      <div className="activity-headline py-4 flex-1 flex flex-col gap-2">
        <ActorInfo {...actor} />
        <ActivityMeta
          {...{
            activityId,
            published,
            isNew,
            actorUpdatedAt,
            actorUpdatedBy,
            archivedAt,
            archivedByName,
            contentUpdatedAt: (activityObject as ObjectWithContent)
              ?.content_updated_at,
            contentUpdatedBy: (activityObject as ObjectWithContent)
              ?.content_updated_by,
            foreignActivityStreams,
            streamsFromActivity,
            error,
          }}
        />
      </div>
      {!isHit && !external && (
        <div className="contxt-menu-view flex -mt-1 -mr-1">
          {Preload.current_network.config?.translations?.deep_l?.enabled &&
            !error &&
            type !== "article" &&
            !window.isApp &&
            translateable && (
              <TranslationControls
                object_language={translatedContent?.language || "ORIGINAL"}
                types={["activity"]}
                target_id={activityId}
                resetTranslation={resetTranslation}
                translateObject={translateObject}
              />
            )}
          {get(activityObject, "i_bookmarked?") && <IBookmarkedIndicator />}
          {(activityObject?.recommendation_count || 0) > 0 && (
            <RecommendationIndicator
              count={activityObject?.recommendation_count || 0}
            />
          )}
          {can?.add_to_newsletter &&
            (plannedForNewsletterAt || sentInNewsLetterAt) &&
            (!streamsFromActivity?.length ||
              !every(streamsFromActivity, (stream) => stream.sensitive)) && (
              <NewsletterIndicator
                plannedForNewsletterAt={plannedForNewsletterAt}
                sentInNewsLetterAt={sentInNewsLetterAt}
              />
            )}
          {(!isEmpty(emailRecommendedTo) ||
            !isEmpty(emailRecommendedToMembers)) &&
            isEmpty(externalEmailRecommendedTo) &&
            some(
              streamsFromActivity,
              (stream) => stream.can?.email_recommend_activity_internally,
            ) && (
              <EmailRecommendedIndicator
                lastRecommendation={emailRecommendationInfo?.lastRecommendation}
                emailRecommendedTo={emailRecommendedTo}
                emailRecommendedToMembers={emailRecommendedToMembers}
                externalEmailRecommendedTo={externalEmailRecommendedTo}
              />
            )}
          {!error && !window.isApp && (
            <ActivityContextMenu
              activityId={activityId}
              emailRecommendationCount={emailRecommendationCount}
              emailRecommendationInfo={emailRecommendationInfo}
              streamsFromActivity={streamsFromActivity}
              repliesClosed={repliesClosed}
              hidden={hidden}
              teaser={teaser}
              activityCan={can}
              objectId={activityObject?.id}
              plannedForNewsletterAt={plannedForNewsletterAt}
              sticky={sticky}
              archivedAt={archivedAt}
              canShare={canShare}
              shareActivity={shareActivity}
              iSubscribedEmail={iSubscribedEmail}
              streamUrl={streamUrl}
              closable={get(activityObject, "closable?")}
              iBookmarked={get(activityObject, "i_bookmarked?")}
              iRecommend={get(activityObject, "i_recommend?")}
              actor={actor}
              {...(omit(activityObject, [
                "id",
                "closable?",
                "i_bookmarked?",
                "i_recommend?",
              ]) as Omit<
                ActivityObject,
                "id" | "closable?" | "i_bookmarked?" | "i_recommend?"
              >)}
            />
          )}
        </div>
      )}
      {renderedAsPinnedDuplicate && (
        <div>
          <button
            role="button"
            className="btn btn-sm p-0"
            onClick={closeActivity}
          >
            <FontAwesomeIcon icon={faAnglesUp} />
          </button>
        </div>
      )}
      {error && (
        <FontAwesomeIcon
          className="text-danger py-4 mr-4"
          icon={faTriangleExclamation}
          size="2x"
        />
      )}
    </div>
  );
}
